import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { SgKdConstants } from '../../../../../sg-kd-shared/sg-kd-constants';
import { ZipcodeFormatService } from '../../../../../sg-kd-shared/services/zipcode-format.service';
import { UsedSgkd019dSgkd041aService } from '../../services/used-sgkd019d-sgkd041a.service';
import { takeUntil } from 'rxjs/operators';
import { Sgkd041aResponse } from 'sg-api';

@Component({
  selector: 'app-sgkd019d-hakkomoto',
  templateUrl: './sgkd019d-hakkomoto.component.html',
  styleUrl: './sgkd019d-hakkomoto.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgkd019dHakkomotoComponent implements OnInit, OnDestroy {

  private onDestroy$ = new Subject();

  /** メインフォーム */
  mainForm: UntypedFormGroup;
  /** 発行元フォーム */
  hakkomotoForm: UntypedFormGroup;

  /** メッセージクリアイベント */
  @Output() clearMessageEvent = new EventEmitter();

  /** 発行元情報取得APIレスポンス */
  private response: Sgkd041aResponse;

  constructor(
    private parent: FormGroupDirective,
    private formBuilder: UntypedFormBuilder,
    private zipcodeFormatService: ZipcodeFormatService,
    private usedSgkd019dSgkd041aService: UsedSgkd019dSgkd041aService) {
  }

  /**
   * 初期処理
   * 
   * @memberof Sgkd019dHakkomotoComponent
   */
  ngOnInit(): void {
    this.mainForm = this.parent.form;
    this.hakkomotoForm = this.createForm();
    this.mainForm.addControl('hakkomotoForm', this.hakkomotoForm);
    this.usedSgkd019dSgkd041aService.loadHakkomoto()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        response => {
          this.response = response;
          this.setControlForm(response);
        }, error => {
        }
      );
  }

  /**
   * 破棄処理
   * 
   * @memberof Sgkd019dHakkomotoComponent
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    if (this.mainForm) {
      this.mainForm.clearValidators();
      this.mainForm.updateValueAndValidity();
      this.mainForm.removeControl('hakkomotoForm');
    }
  }

  /**
   * クリアボタン押下処理
   * 
   * @memberof Sgkd019dSendtoInfoComponent 
   */
  onClickClear(): void {
    this.clearControlForm();
  }

  /**
   * 郵便番号フォーカスアウト
   * 
   * @memberof Sgkd019dSendtoInfoComponent 
   */
  onBlurZipcode() {
    let targetZipcode = this.hakkomotoForm.get('hakkomotoPostno').value;
    let hyphenZipcode = this.zipcodeFormatService.formatZipcode(targetZipcode);
    this.hakkomotoForm.get('hakkomotoPostno').setValue(hyphenZipcode);
  }

  /**
   * 郵便番号フォーカスイン
   * 
   * @memberof Sgkd019dSendtoInfoComponent 
   */
  onFocusZipcode() {
    let targetZipcode = this.hakkomotoForm.get('hakkomotoPostno').value;
    let noHyphenZipcode = this.zipcodeFormatService.reformatZipcode(targetZipcode);
    this.hakkomotoForm.get('hakkomotoPostno').setValue(noHyphenZipcode);
  }

  /**
   * Formの生成処理
   * 
   * @returns 本コンポーネントのForm情報
   * @memberof Sgkd019dHakkomotoComponent
   */
  private createForm(): UntypedFormGroup {
    let form = this.formBuilder.group({
      /** 発行元 - 郵便番号 */
      hakkomotoPostno: new UntypedFormControl({ value: '', disabled: false },
        [
          Validators.required,
          Validators.maxLength(SgKdConstants.SGKD019D.MAX_LENGTH.POST_NO),
          Validators.pattern(SgKdConstants.PATTERN.POST_NO)
        ]),
      /** 発行元 - 住所 */
      hakkomotoAddr: new UntypedFormControl({ value: '', disabled: false },
        [
          Validators.required,
          Validators.maxLength(SgKdConstants.SGKD019D.MAX_LENGTH.HAKKOMOTO_ADDR)
        ]),
      /** 発行元 - 問い合わせ窓口 */
      contactMadoguchi: new UntypedFormControl({ value: '', disabled: false },
        [
          Validators.required,
          Validators.maxLength(SgKdConstants.SGKD019D.MAX_LENGTH.CONTACT_MADOGUCHI)
        ]),
      /** 発行元 - 電話番号 */
      hakkomotoTelno: new UntypedFormControl({ value: '', disabled: false },
        [
          Validators.required,
          Validators.maxLength(SgKdConstants.SGKD019D.MAX_LENGTH.TEL_NO),
          Validators.pattern(SgKdConstants.PATTERN.TEL_NO)
        ])
    });
    return form;
  }

  /**
   * 発行元のクリア処理
   * 
   * @memberof Sgkd019dHakkomotoComponent
   */
  private clearControlForm(): void {
    this.setControlForm(this.response);
  }

  /**
   * 発行元の設定処理
   * 
   * @param info 発行元情報
   * @memberof Sgkd019dHakkomotoComponent
   */
  private setControlForm(info: Sgkd041aResponse) {
    // 発行元 - 郵便番号
    this.hakkomotoForm.get('hakkomotoPostno').setValue(info.responseDto.postno);
    this.onBlurZipcode();
    // 発行元 - 住所
    let addr = "";
    addr += info.responseDto.mptAname ?? "";
    addr += info.responseDto.townAreaName ?? "";
    addr += info.responseDto.ooazaName ?? "";
    addr += info.responseDto.daihyobanti ?? "";
    addr += info.responseDto.daihyobldngname ?? "";
    this.hakkomotoForm.get('hakkomotoAddr').setValue(addr);
    // 発行元 - 問い合わせ窓口
    this.hakkomotoForm.get('contactMadoguchi').setValue(info.responseDto.sosikiformalname);
    // 発行元 - 電話番号
    this.hakkomotoForm.get('hakkomotoTelno').setValue(info.responseDto.daihyotelno?.trim());
  }
}
